/*
 * Reliable photo handling and upload for the mosaic creator.
 *
 * In-app browsers (Instagram, Facebook, Pinterest) regularly deliver empty
 * files or drop parts of a large upload. This module:
 *  - validates and (in in-app browsers) re-draws the main photo, so we never
 *    send an empty or undecodable main image;
 *  - uploads the main photo first, then tiles in small batches with retries
 *    (POST /upload, then POST /upload?sessionId=...);
 *  - falls back to the single-request upload when the backend is older;
 *  - turns failures into error codes with a customer-facing message.
 *
 * Every step is reported through window.mosafyTrack (assets/mosafy-funnel.js).
 */
(function (root) {
  const IN_APP_BROWSERS = [
    [/Instagram/i, 'instagram'],
    [/FBAN|FBAV|FB_IAB|FBIOS/i, 'facebook'],
    [/Pinterest/i, 'pinterest'],
    [/TikTok|musical_ly|BytedanceWebview/i, 'tiktok'],
    [/Snapchat/i, 'snapchat'],
    [/LinkedInApp/i, 'linkedin'],
  ];

  const MAX_MAIN_SIDE = 4096; // Stays under iOS canvas limits (16.7 MP)
  const TILE_BATCH_SIZE = 10;
  const REQUEST_TIMEOUT_MS = 120000;

  class UploadError extends Error {
    constructor(code, detail) {
      super(code);
      this.code = code;
      this.detail = detail;
    }
  }

  function detectInAppBrowser(ua) {
    for (const [pattern, name] of IN_APP_BROWSERS) {
      if (pattern.test(ua || '')) return name;
    }
    return null;
  }

  function isAndroid(ua) {
    return /Android/i.test(ua || '');
  }

  // Opens the current page in Chrome from an Android in-app browser.
  function chromeIntentUrl(href) {
    const url = new URL(href);
    return `intent://${url.host}${url.pathname}${url.search}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(href)};end`;
  }

  function track(step, detail = {}) {
    if (typeof root.mosafyTrack === 'function') return root.mosafyTrack(step, detail);
    try {
      root.dispatchEvent(new CustomEvent('mosafy:creator', { detail: { step, inApp: detectInAppBrowser(root.navigator?.userAgent), ...detail } }));
    } catch (_) {
      // Analytics must never break the creator
    }
  }

  function readFile(file) {
    if (typeof file.arrayBuffer === 'function') {
      return file.arrayBuffer().catch(() => readFileWithReader(file));
    }
    return readFileWithReader(file);
  }

  function readFileWithReader(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });
  }

  function loadImage(blob) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new UploadError('main_unreadable'));
      };
      img.src = url;
    });
  }

  function drawScaled(img, maxSide) {
    const ratio = Math.min(maxSide / img.width, maxSide / img.height, 1);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * ratio);
    canvas.height = Math.round(img.height * ratio);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas;
  }

  function canvasToBlob(canvas, quality) {
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        canvas.width = 0;
        canvas.height = 0;
        resolve(blob && blob.size > 0 ? blob : null);
      }, 'image/jpeg', quality);
    });
  }

  /**
   * Reads and checks the main photo. Returns the blob to upload plus a
   * display thumbnail. Throws UploadError('main_unreadable') when the photo
   * is empty or cannot be decoded, so the customer can pick it again.
   */
  async function prepareMainImage(file, options = {}) {
    const inApp = options.inApp ?? detectInAppBrowser(root.navigator?.userAgent);
    if (!file || file.size === 0) throw new UploadError('main_unreadable', 'empty file');

    let buffer;
    try {
      buffer = await readFile(file);
    } catch (err) {
      throw new UploadError('main_unreadable', String(err));
    }
    if (!buffer || buffer.byteLength === 0) throw new UploadError('main_unreadable', 'empty read');

    const isJpegOrPng = /^image\/(jpe?g|png)$/i.test(file.type || '');
    const original = new Blob([buffer], { type: isJpegOrPng ? file.type : 'image/jpeg' });
    const img = await loadImage(original);
    const width = img.width;
    const height = img.height;

    // A freshly drawn canvas blob survives in-app browsers far better than the
    // raw picked file; elsewhere keep the original bytes (full print quality).
    let uploadBlob = original;
    if (inApp || !isJpegOrPng) {
      const redrawn = await canvasToBlob(drawScaled(img, MAX_MAIN_SIDE), 0.95);
      if (redrawn) uploadBlob = redrawn;
    }

    const thumbnail = await canvasToBlob(drawScaled(img, 1000), 0.9);
    img.src = '';

    track('main_selected', { width, height, bytes: uploadBlob.size, redrawn: uploadBlob !== original });
    return {
      uploadFile: new File([uploadBlob], file.name || 'main.jpg', { type: 'image/jpeg' }),
      width,
      height,
      thumbnailUrl: URL.createObjectURL(thumbnail || uploadBlob),
    };
  }

  async function postForm(url, formData) {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : null;
    try {
      const response = await fetch(url, { method: 'POST', body: formData, signal: controller?.signal });
      let body = {};
      try {
        body = await response.json();
      } catch (_) {
        // Non-JSON error page (e.g. from a proxy)
      }
      return { status: response.status, ok: response.ok && body.ok !== false, body };
    } catch (err) {
      return { status: 0, ok: false, body: { error: 'network' }, networkError: String(err) };
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  // Retries network failures and server errors; 4xx answers are final.
  async function postWithRetry(url, makeForm, attempts = 3, sleep = (ms) => new Promise((r) => setTimeout(r, ms))) {
    let result;
    for (let attempt = 1; attempt <= attempts; attempt++) {
      result = await postForm(url, makeForm());
      if (result.ok || (result.status >= 400 && result.status < 500)) return result;
      if (attempt < attempts) await sleep(1500 * attempt);
    }
    return result;
  }

  function tilesForm(tiles) {
    const form = new FormData();
    tiles.forEach((tile, i) => form.append('tiles', tile, tile.name || `tile-${i}.jpg`));
    return form;
  }

  function failureCode(result) {
    if (result.body?.error === 'main_missing') return 'main_missing';
    if (result.status === 0) return 'network';
    return 'upload_failed';
  }

  /**
   * Uploads the main photo and tiles. Resolves with { sessionId, totalTiles }.
   * Rejects with an UploadError whose code is one of: main_missing,
   * tiles_missing, network, upload_failed.
   */
  async function uploadSession({ api, main, tiles, onProgress = () => {}, sleep }) {
    const usableTiles = tiles.filter((t) => t && t.size > 0);
    if (!main || main.size === 0) throw new UploadError('main_missing', 'empty before upload');
    if (usableTiles.length === 0) throw new UploadError('tiles_missing');
    track('upload_start', { tiles: usableTiles.length });

    const mainForm = () => {
      const form = new FormData();
      form.append('main', main, main.name || 'main.jpg');
      return form;
    };
    const first = await postWithRetry(`${api}/upload`, mainForm, 3, sleep);
    if (!first.ok) throw new UploadError(failureCode(first), first.networkError || first.status);
    const sessionId = first.body.sessionId;

    // Older backend: no batch support, so send everything in one request.
    if (typeof first.body.totalTiles !== 'number') {
      const legacy = await postWithRetry(`${api}/upload`, () => {
        const form = tilesForm(usableTiles);
        form.append('main', main, main.name || 'main.jpg');
        return form;
      }, 2, sleep);
      if (!legacy.ok || legacy.body.mainImage === false) {
        throw new UploadError(legacy.body.mainImage === false ? 'main_missing' : failureCode(legacy));
      }
      onProgress(1);
      track('upload_done', { tiles: legacy.body.tilesUploaded, batched: false });
      return { sessionId: legacy.body.sessionId, totalTiles: legacy.body.tilesUploaded };
    }

    let totalTiles = 0;
    const lost = [];
    const appendUrl = `${api}/upload?sessionId=${encodeURIComponent(sessionId)}`;
    for (let i = 0; i < usableTiles.length; i += TILE_BATCH_SIZE) {
      const batch = usableTiles.slice(i, i + TILE_BATCH_SIZE);
      const result = await postWithRetry(appendUrl, () => tilesForm(batch), 3, sleep);
      if (!result.ok) throw new UploadError(failureCode(result), result.networkError || result.status);
      totalTiles = result.body.totalTiles;
      // Files the server received empty: send those once more on their own
      const skipped = new Set(result.body.skippedFiles || []);
      if (skipped.size) lost.push(...batch.filter((t) => skipped.has(t.name)));
      onProgress(Math.min(i + TILE_BATCH_SIZE, usableTiles.length) / usableTiles.length);
    }

    if (lost.length) {
      const retry = await postWithRetry(appendUrl, () => tilesForm(lost), 2, sleep);
      if (retry.ok) totalTiles = retry.body.totalTiles;
      track('tiles_resent', { count: lost.length, ok: retry.ok });
    }

    if (totalTiles === 0) throw new UploadError('tiles_missing');
    track('upload_done', { tiles: totalTiles, sent: usableTiles.length, batched: true });
    return { sessionId, totalTiles };
  }

  const MESSAGES = {
    nl: {
      main_unreadable: 'We konden deze foto niet openen. Kies de foto opnieuw of probeer een andere foto.',
      main_missing: 'Je hoofdfoto is niet goed aangekomen. Kies je hoofdfoto opnieuw en probeer het nog eens.',
      tiles_missing: 'Je tegelfoto\'s zijn niet goed aangekomen. Voeg ze opnieuw toe en probeer het nog eens.',
      network: 'De verbinding viel weg tijdens het uploaden. Controleer je internet en probeer het opnieuw.',
      generate_failed: 'Er ging iets mis bij het maken van je mozaïek. Probeer het over een paar seconden opnieuw.',
      upload_failed: 'Het uploaden is niet gelukt. Probeer het over een paar seconden opnieuw.',
      in_app_hint: 'Tip: open deze pagina in je browser (Safari of Chrome) voor het soepelst uploaden van je foto\'s.',
      tiles_skipped: (n) => `${n} foto${n === 1 ? '' : '\'s'} kon${n === 1 ? '' : 'den'} niet worden geopend en ${n === 1 ? 'is' : 'zijn'} overgeslagen.`,
    },
    en: {
      main_unreadable: 'We couldn\'t open this photo. Please choose it again or try another photo.',
      main_missing: 'Your main photo didn\'t come through. Please choose your main photo again and retry.',
      tiles_missing: 'Your tile photos didn\'t come through. Please add them again and retry.',
      network: 'The connection dropped while uploading. Check your internet connection and try again.',
      generate_failed: 'Something went wrong while creating your mosaic. Please try again in a few seconds.',
      upload_failed: 'The upload didn\'t work. Please try again in a few seconds.',
      in_app_hint: 'Tip: open this page in your browser (Safari or Chrome) for the smoothest photo upload.',
      tiles_skipped: (n) => `${n} photo${n === 1 ? '' : 's'} couldn't be opened and ${n === 1 ? 'was' : 'were'} skipped.`,
    },
  };

  function message(code, lang, arg) {
    const set = MESSAGES[(lang || '').toLowerCase().startsWith('en') ? 'en' : 'nl'];
    const text = set[code] ?? set.upload_failed;
    return typeof text === 'function' ? text(arg) : text;
  }

  const api = {
    UploadError,
    detectInAppBrowser,
    isAndroid,
    chromeIntentUrl,
    prepareMainImage,
    uploadSession,
    message,
    track,
    TILE_BATCH_SIZE,
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.MosafyUpload = api;
})(typeof window !== 'undefined' ? window : globalThis);
